import { ApolloError } from "@apollo/client";
import { Box, Flex, StyleProps } from "@chakra-ui/react";
import React, { useCallback, useRef, useState } from "react";

import { Alert, EmptyBrowserIcon, LoadingIndicator } from "../../../components";
import { getErrorInfo, isCustomError } from "../../../utils/apollo/error";
import {
  AiInterviewerListInterviewItem,
  AiInterviewerListInterviewItemType,
} from "./AiInterviewerListInterviewItem";

type AiInterviewerListInterviewsProps = StyleProps & {
  interviews: AiInterviewerListInterviewItemType[];
  canEdit?: boolean;
  emptyMessage?: React.ReactNode;
  loading?: boolean;
  error?: ApolloError | string;
  footer?: React.ReactNode;
  scrollRef?: (node: HTMLDivElement | null) => void;
  /** Show a loading overlay over the list content (useful when filters are being applied) */
  showLoadingOverlay?: boolean;
};

export const AiInterviewerListInterviews: React.FC<
  AiInterviewerListInterviewsProps
> = ({
  interviews,
  canEdit,
  emptyMessage,
  loading,
  error,
  footer,
  scrollRef,
  showLoadingOverlay,
  ...styles
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setIsScrolled(e.currentTarget.scrollTop > 0);
  }, []);

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      scrollContainerRef.current = node;
      scrollRef?.(node);
    },
    [scrollRef]
  );

  if (loading) {
    return (
      <Flex h="100%" {...styles}>
        <LoadingIndicator m="auto" pos="relative" top="-25%" />
      </Flex>
    );
  }

  if (error) {
    let errorMessage = "";
    if (typeof error === "string") {
      errorMessage = error;
    } else {
      const { message, code } = getErrorInfo(error);
      errorMessage = isCustomError(error)
        ? message
        : code === "FORBIDDEN"
        ? "You are not authorized to access this page."
        : "An error occurred while loading the page.";
    }
    return (
      <Alert
        status="error"
        title="Error"
        description={errorMessage}
        {...styles}
      />
    );
  }

  if (interviews.length === 0) {
    return (
      <Flex
        bg="blackAlpha.50"
        borderRadius="xl"
        direction="column"
        gap="3"
        align="center"
        justify="start"
        pt="16"
        pb="32"
        {...styles}
      >
        <EmptyBrowserIcon />
        <Box maxW={360} color="gray.500" textAlign="center">
          {emptyMessage ||
            "No candidates to show yet. They will appear here after they have been invited."}
        </Box>
      </Flex>
    );
  }

  return (
    <Box position="relative" minH="0" data-testid="candidates-list" {...styles}>
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        height="16px"
        background={
          isScrolled
            ? "linear-gradient(to bottom, rgba(0, 0, 0, 0.1), transparent)"
            : "transparent"
        }
        pointerEvents="none"
        zIndex={10}
        transition="opacity 0.2s ease, background 0.2s ease"
      />
      <Flex
        direction="column"
        gap="3"
        overflowY="auto"
        overflowX="hidden"
        minW="0"
        h="100%"
        // extra spacing on the right for the scrollbar
        pr="4"
        mr="-4"
        ref={setRefs}
        onScroll={handleScroll}
      >
        {interviews.map((interview) => (
          <AiInterviewerListInterviewItem
            key={interview.id}
            canEdit={canEdit}
            {...interview}
          />
        ))}
        {footer}
      </Flex>
      {showLoadingOverlay && (
        <Flex
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="whiteAlpha.700"
          backdropFilter="blur(4px)"
          zIndex={11}
          pointerEvents="none"
          align="center"
          justify="center"
        >
          <LoadingIndicator size="lg" />
        </Flex>
      )}
    </Box>
  );
};
